import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import React from 'react';
import { addCommas } from '@ecs/tng-common/scripts/helpers';
import PersonalLoanCalc from '../components/calculators/PersonalLoanCalc';
import APRCalc from '../components/calculators/AprCalc';
import SavingsCalc from '../components/calculators/SavingsCalc';
import MortgageCalc from '../components/calculators/MortgageCalc';
import CarPaymentCalc from '../components/calculators/CarPaymentCalc';
import CreditCardCalc from '../components/calculators/CreditCardCalc';
import ReverseCarCalc from '../components/calculators/ReverseCarCalc';
import { CalculatorTypes } from './interfaces';

export const calcMarkerId = 'calc-embed';
export const calcMarker = `<div id="${calcMarkerId}"></div>`;

export const isCalculatorType = (value: CalculatorTypes) => {
  const calculatorTypes: CalculatorTypes[] = [
    'apr-inc',
    'creditcard-multi-inc',
    'mortgage-inc',
    'personal-loan-inc',
    'car-payment-inc',
    'savings-inc',
    'reverse-car'
  ];
  return calculatorTypes.includes(value);
};
export type CalculatorProps = {
  isFull?: boolean;
};
export const chartColors = {
  blue: '#2A5595',
  green: '#49c783'
};
export interface DataPoint {
  name: string;
  y: number;
  color: string;
}
export const returnCalculatorType = (calculator: CalculatorTypes, isFullCalculator: boolean) => {
  switch (calculator) {
    case 'personal-loan-inc':
      return <PersonalLoanCalc isFull={isFullCalculator} />;
    case 'apr-inc':
      return <APRCalc isFull={isFullCalculator} />;
    case 'savings-inc':
      return <SavingsCalc isFull={isFullCalculator} />;
    case 'creditcard-multi-inc':
      return <CreditCardCalc isFull={isFullCalculator} />;
    case 'mortgage-inc':
      return <MortgageCalc isFull={isFullCalculator} />;
    case 'car-payment-inc':
      return <CarPaymentCalc isFull={isFullCalculator} />;
    case 'reverse-car':
      return <ReverseCarCalc isFull={isFullCalculator} />;
    default:
      return null;
  }
};

export function setHighChartOptions(data: DataPoint[]) {
  return {
    chart: {
      type: 'pie',
      height: 135,
      width: 135,
      className: 'flex-shrink-0 flex-grow-0 highcharts-size',
      marginLeft: 5
    },
    title: {
      text: ''
    },
    plotOptions: {
      pie: {
        size: '130',
        innerSize: '85',
        dataLabels: {
          enabled: false // Disable data labels on the chart
        },
        center: ['50%', '50%'],
        showInLegend: false,
        states: {
          hover: {
            halo: null // Disable hover state
          }
        }
      }
    },
    tooltip: {
      pointFormat: '{series.name}: <b>{point.percentage:.1f}%</b>'
    },
    series: [
      {
        name: 'Total',
        data: data
      }
    ],
    accessibility: {
      enabled: true,
      description:
        'A pie chart illustrating the distribution of the total principal and interest payments.',
      point: {
        valueSuffix: '%'
      },
      keyboardNavigation: {
        enabled: true
      }
    }
  } as Highcharts.Options;
}
interface IDrawHighChart {
  title: string;
  option1: {
    name: string;
    value: number;
  };
  option2: {
    name: string;
    value: number;
  };
}
export function calcMonthlyInterestRate(interestRate: number) {
  return interestRate / 100 / 12;
}
export function calcAPR(
  numPayments: number,
  monthlyRate: number,
  loanAmount: number,
  finCharges: number
) {
  const totalmonthly =
    ((loanAmount + finCharges) * monthlyRate * (1 + monthlyRate) ** numPayments) /
    ((1 + monthlyRate) ** numPayments - 1);
  let trate = monthlyRate;
  let i = 1;
  let tresult = 0;
  let tdiff = trate;

  while (i <= 100) {
    tresult =
      (trate * (1 + trate) ** numPayments) / ((1 + trate) ** numPayments - 1) -
      totalmonthly / loanAmount;

    if (Math.abs(tresult) < 0.0000001) {
      break;
    }

    if (tresult < 0) {
      trate += tdiff;
    } else {
      trate -= tdiff;
    }

    tdiff /= 2;
    i++;
  }
  trate = trate * 12 * 100;

  return trate;
}

export function calcMonthlyPayment(loanAmount: number, monthlyInterest: number, term: number) {
  const x = (1 + monthlyInterest) ** term;
  return (loanAmount * x * monthlyInterest) / (x - 1);
}
export function calcCarLoanAmount(mPayment: number, monthlyInterest: number, term: number) {
  const x = (1 + monthlyInterest) ** term;
  return (mPayment * (x - 1)) / (x * monthlyInterest);
}
export function interestOnDeposit(
  deposit: number,
  apyPersentage: number,
  period: number,
  compFreq: number
) {
  return deposit * (1 + apyPersentage / compFreq) ** (period * compFreq);
}
export function earningOnFutureContribution(
  contribution: number,
  apyPersentage: number,
  period: number,
  compFreqN: number
) {
  const t = compFreqN * period;
  return (
    contribution *
    (12 / compFreqN) *
    (((1 + apyPersentage / compFreqN) ** t - 1) / (apyPersentage / compFreqN))
  );
}

export function drawHighChart(conf: IDrawHighChart) {
  return (
    <figure
      className="mb-0 col-md-6 col-lg-12 col-xl-6"
      aria-labelledby="chart-title"
      aria-describedby="chart-legend">
      <div className="d-flex align-items-center justify-content-start pb-2">
        <div className="highcharts-size flex-shrink-0">
          <HighchartsReact
            highcharts={Highcharts}
            options={setHighChartOptions([
              {
                name: conf.option1.name,
                y: conf.option1.value,
                color: chartColors.blue
              },
              {
                name: conf.option2.name,
                y: conf.option2.value,
                color: chartColors.green
              }
            ])}
          />
        </div>
        <h5
          id="chart-title"
          className="mb-0">
          {conf.title}
        </h5>
      </div>
      <figcaption id="chart-legend">
        <div className="border-bottom d-flex justify-content-between py-2 py-lg-3">
          <span className="text-gray">
            <span className="marker bg-secondary" />
            {conf.option1.name}:
          </span>
          <span className="fw-bold">{addCommas(conf.option1.value)}%</span>
        </div>
        <div className="d-flex justify-content-between pt-2 pt-lg-3">
          <span className="text-gray">
            <span className="marker bg-green" />
            {conf.option2.name}:
          </span>
          <span className="fw-bold">{addCommas(conf.option2.value)}%</span>
        </div>
      </figcaption>
    </figure>
  );
}
